import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Autocomplete,
  TextField,
  IconButton,
  Box,
  useTheme,
  CircularProgress,
  FormControl,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { BoxOwnProps } from "@mui/system";
import classNames from "classnames";
import * as lodash from "lodash";
import * as React from "react";

import { LoggerService } from "@/shared/services";

import { useElementsForAutocompleteType } from "./hook";
import styles from "./TagAndMetadataAutocomplete.module.css";
import { AutocompleteType } from "./types";

const INPUT_TEXT_FONT_SIZE = "0.9rem";
const REGULAR_INPUT_WIDTH = "125px";
const SMALLER_INPUT_WIDTH = "100px";
const MAX_AUTOCOMPLETE_OPTIONS_WIDTH = "200px";

interface TagAndMetadataAutocompleteProps {
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => void;
  onTagCreated?: (tag: string) => void;
  onMetadataCreated?: (key: string, value: unknown) => void;
  // If existingTags is provided, the autocomplete will filter out these tags.
  existingTags?: string[];
  existingMetadata?: Record<string, unknown>;
  autocompleteType: AutocompleteType;
  shrink?: boolean;
  sx?: BoxOwnProps<Theme>;
  hideAddButton?: boolean;
  clearInputOnSelect?: boolean;
  keyPlaceholderText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  jsonMetadataDisabled?: boolean;
}

enum Mode {
  Tags = "tags",
  Metadata = "metadata",
}

export const TagAndMetadataAutocomplete: React.FC<
  TagAndMetadataAutocompleteProps
> = ({
  onChange,
  onTagCreated,
  onMetadataCreated,
  autocompleteType,
  shrink,
  existingTags,
  existingMetadata,
  sx,
  hideAddButton,
  clearInputOnSelect = true,
  keyPlaceholderText,
  disabled = false,
  isLoading = false,
  jsonMetadataDisabled = false,
}) => {
  const theme = useTheme();

  if (!existingTags && !existingMetadata) {
    LoggerService.error(
      "existingTags or existingKeys must be provided to TagAndMetadataAutocomplete",
    );
  }

  if (existingTags && existingMetadata) {
    LoggerService.error(
      "existingTags and existingKeys cannot both be provided to TagAndMetadataAutocomplete",
    );
  }

  // Fire every time existing tags change. Make sure the autocomplete is always up to date.
  const { elements: retrievedElements } = useElementsForAutocompleteType(
    autocompleteType,
    existingTags,
  );

  let elements: string[] = retrievedElements;

  // Add existing keys to the autocomplete options
  const existingKeys = existingMetadata && Object.keys(existingMetadata);
  if (existingKeys) {
    const elementSet = new Set(elements);
    existingKeys.forEach((key) => {
      elementSet.add(key);
    });

    elements = Array.from(elementSet);
    elements = elements.sort();
  }

  let placeholder: string;
  let mode: Mode;

  switch (autocompleteType) {
    case AutocompleteType.ActionTags:
    case AutocompleteType.CollectionTags:
    case AutocompleteType.DatasetTags:
    case AutocompleteType.EventTags:
    case AutocompleteType.FileTags:
    case AutocompleteType.GenericTags:
    case AutocompleteType.SessionTags:
    case AutocompleteType.SkillTags:
      placeholder = "Tag";
      mode = Mode.Tags;
      break;
    case AutocompleteType.ActionEnvVarKeys:
    case AutocompleteType.ActionMetadataKeys:
    case AutocompleteType.DatasetMetadataKeys:
    case AutocompleteType.EventMetadataKeys:
    case AutocompleteType.FileMetadataKeys:
    case AutocompleteType.GenericMetadataKeys:
    case AutocompleteType.SessionMetadataKeys:
    case AutocompleteType.TopicMetadataKeys:
      placeholder = "Key";
      mode = Mode.Metadata;
      break;
    default:
      // Reachable: this switch is not exhaustive over `AutocompleteType`
      // (`EventNames` has never been handled), so a member missing from the
      // lists above throws here the first time it is rendered rather than
      // failing to compile. Naming the value is what turns that into a
      // one-line diagnosis instead of a hunt.
      throw new Error(
        `Unsupported autocomplete type: ${String(autocompleteType)}`,
      );
  }

  if (keyPlaceholderText) {
    placeholder = keyPlaceholderText;
  }

  const [inputValue, setInputValue] = React.useState<string>("");
  const [metadataValue, setMetadataValue] = React.useState<string>("");
  const [selectedValue, setSelectedValue] = React.useState<string | null>(null);
  const noKeyError = !inputValue && "Key missing";
  const noValueError = !metadataValue && "Value missing";

  if (mode === Mode.Tags && existingTags) {
    elements = elements.filter((tag) => !existingTags.includes(tag));
  }

  const [useJsonInput, setUseJsonInput] = React.useState(false);
  const [userChoseJsonInput, setUserChoseJsonInput] = React.useState(false);
  const [jsonError, setJsonError] = React.useState("");
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  React.useEffect(
    function validateMetadataValue() {
      if (useJsonInput) {
        setJsonError(parseOrExplainJSON(metadataValue));
      } else {
        setJsonError("");
      }
    },
    [useJsonInput, metadataValue],
  );
  const handleElementChosen = (element: string) => {
    const cleanElement = element.trim();

    if (cleanElement) {
      if (mode === Mode.Tags) {
        if (clearInputOnSelect) {
          setInputValue("");
          setSelectedValue(null);
        } else {
          setInputValue(cleanElement);
          setSelectedValue(cleanElement);
        }

        if (onTagCreated && !existingTags?.includes(cleanElement)) {
          onTagCreated(cleanElement);
        }
      } else if (mode === Mode.Metadata) {
        setInputValue(cleanElement);
      }
    }
  };

  const handleMetadataCreated = (key: string, value: string | object) => {
    setSubmitAttempted(true);
    if (!onMetadataCreated || !key || !value) {
      return;
    }
    if (useJsonInput) {
      if (jsonError) {
        return;
      }
      if (typeof value === "string") {
        try {
          value = JSON.parse(value) as object;
        } catch {
          setJsonError(parseOrExplainJSON(metadataValue));
          return;
        }
      }
    }

    setInputValue("");
    setMetadataValue("");
    setSelectedValue(null);
    const nestedKeys = key.split(".");
    const allowJsonMetadata = !jsonMetadataDisabled;
    if (allowJsonMetadata && nestedKeys.length > 1) {
      // Turn the value into a nested object of all the keys.
      key = nestedKeys[0];
      // Iterate from inside to outside, nesting the value inside the key segments.
      for (const k of nestedKeys.slice(1).reverse()) {
        value = { [k]: value };
      }
      // Merge the new maybe-nested object with whatever existing metadata there is.
      if (existingMetadata && typeof existingMetadata?.[key] === "object") {
        // Recursively merge the metadata.
        value = lodash.merge({}, existingMetadata[key], value);
      }
    }
    onMetadataCreated(key, value);
    if (!userChoseJsonInput) {
      setUseJsonInput(false);
    }
    setSubmitAttempted(false);
  };

  const errorText: string | null = submitAttempted
    ? jsonError || noKeyError || noValueError || null
    : null;
  return (
    <>
      <FormControl>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: theme.spacing(1),
            ...sx,
          }}
        >
          <Autocomplete
            data-cy="tagAndMetadataAutocomplete"
            onChange={(_, value) => {
              if (value) {
                handleElementChosen(value);
              }
            }}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") {
                // Prevent MUI default 'Enter' behavior.
                ev.defaultMuiPrevented = true;
                ev.preventDefault();
                // Now our handler code
                const evTarget = ev.target as HTMLInputElement;

                if (evTarget?.value) {
                  const tagValue = evTarget.value;
                  if (tagValue && typeof tagValue === "string") {
                    handleElementChosen(tagValue);
                  }
                }
              }
            }}
            inputValue={inputValue}
            value={selectedValue}
            onInputChange={(event, newInputValue, reason) => {
              if (onChange) {
                onChange(
                  event as React.ChangeEvent<HTMLInputElement>,
                  newInputValue,
                );
              }

              // onChange also fires above in reset case. We don't want to handle it twice.
              if (reason !== "reset") {
                setInputValue(newInputValue);
              }
            }}
            freeSolo={true}
            size="small"
            sx={{
              width: shrink ? SMALLER_INPUT_WIDTH : REGULAR_INPUT_WIDTH,
              ...sx,
            }}
            options={elements}
            disabled={disabled}
            renderInput={(params) => {
              return (
                <TextField
                  data-cy="tagAndMetadataAutocompleteInput"
                  {...params}
                  error={submitAttempted && !inputValue}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      fontSize: INPUT_TEXT_FONT_SIZE,
                    },
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: {
                      height: shrink ? "1.1em" : "auto",
                    },
                  }}
                  placeholder={placeholder}
                  onBlur={(ev) => {
                    if (ev.target.value) {
                      handleElementChosen(ev.target.value);
                    }
                  }}
                />
              );
            }}
            componentsProps={{
              paper: {
                sx: {
                  fontSize: "0.875rem",
                },
              },
              popper: {
                placement: "bottom-start",
                style: {
                  minWidth: REGULAR_INPUT_WIDTH,
                  maxWidth: MAX_AUTOCOMPLETE_OPTIONS_WIDTH,
                },
              },
            }}
          />
          {mode === Mode.Metadata && (
            <TextField
              data-cy="metadataValue"
              className={classNames([
                styles.sizeTransition,
                useJsonInput ? styles.wideWidth : styles.regularWidth,
              ])}
              size="small"
              variant="outlined"
              placeholder={useJsonInput ? "JSON" : "Value"}
              disabled={disabled}
              InputProps={{
                className: styles.inputTextFont,
                autoComplete: "off",
              }}
              inputProps={{
                className: styles.inputTextFont,
              }}
              value={metadataValue}
              error={submitAttempted && (!metadataValue || !!jsonError)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const mightBeJson =
                  event.target.value.startsWith("{") ||
                  event.target.value.startsWith("[");
                if (jsonMetadataDisabled) {
                  if (useJsonInput) {
                    setUseJsonInput(false);
                    setUserChoseJsonInput(false);
                  }
                } else if (mightBeJson && !useJsonInput) {
                  // Eagerly opt-in to JSON input
                  setUseJsonInput(true);
                  setUserChoseJsonInput(false);
                } else if (!mightBeJson && useJsonInput) {
                  // Intelligently disable JSON input if the user hasn't explicitly enabled it.
                  if (!userChoseJsonInput) {
                    setUseJsonInput(false);
                  }
                }
                setMetadataValue(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  // Prevent default 'Enter' behavior.
                  if (event.key === "Enter") {
                    event.preventDefault();
                  }
                  const ev = event.target as HTMLInputElement;
                  handleMetadataCreated(inputValue, ev.value);
                }
              }}
            />
          )}
          {mode === Mode.Metadata && !jsonMetadataDisabled && (
            <IconButton
              size="small"
              onClick={() => {
                const next = !useJsonInput;
                setUseJsonInput(next);
                setUserChoseJsonInput(next);
              }}
              aria-label="toggle JSON input"
              title={useJsonInput ? "Disable JSON input" : "Enable JSON input"}
              className={classNames(styles.jsonButton, {
                [styles.active]: useJsonInput,
              })}
            >
              JSON
            </IconButton>
          )}
          {!hideAddButton && (
            <IconButton
              data-cy="createAddButton"
              aria-label="add-tag"
              size="small"
              onClick={() => {
                if (mode === Mode.Tags) {
                  handleElementChosen(inputValue);
                } else {
                  handleMetadataCreated(inputValue, metadataValue);
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <AddCircleOutlineIcon color="primary" fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" mt={theme.spacing(0.5)}>
          {errorText && (
            <p
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                color: theme.palette.error.main,
              }}
            >
              {errorText}
            </p>
          )}
        </Typography>
      </FormControl>
    </>
  );
};

/**
 * Parses JSON or explains the cause of its error.
 */
function parseOrExplainJSON(
  jsonString: string,
  options?: { verbose: boolean },
): string {
  function sentenceCase(s: string) {
    return `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
  }
  try {
    JSON.parse(jsonString);
  } catch (error: unknown) {
    // Extract the line and position from the message (e.g., "at position 45")
    const e = error as Error;
    const match = e.message.match(
      /JSON.parse: (.*) (at line (\d+) column (\d+))/,
    );
    const lines = jsonString.split("\n");

    if (match) {
      const details = sentenceCase(match[1]);
      const location = match[2];
      const line = parseInt(match[3]);
      const column = parseInt(match[4]);
      const lineText = lines.at(line - 1) ?? "";
      const locationIndicator = `${"-".repeat(column - 1)}^`;
      const detailed =
        `SyntaxError: ${location}:\n` +
        `${lineText}\n` +
        `${locationIndicator}\n` +
        details;
      const brief = `SyntaxError: ${details} ${location}`;
      return !options?.verbose ? detailed : brief;
    }
    return e.message;
  }
  return ""; // No error.
}
